import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { PERMISSION_KEYS } from "@convex/permissions";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type Control, useForm } from "react-hook-form";

import { ShowWithTransition } from "~/components/shared/show-with-transition";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { DialogClose, DialogFooter } from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { convexClient } from "~/integrations/convex/provider";
import { ProductEvents } from "~/lib/posthog";
import {
  permissionNames,
  permissionsSchema,
} from "~/lib/validators/merchant-permissions";

// Avoids TS2589 (type instantiation is excessively deep) from api.d.ts chain
const updateRoleRef = api.organizationRoles.updateRole;

const createRoleRef = api.organizationRoles.createRole;

interface RoleFormData {
  name: string;
  permissions: {
    [key in keyof typeof permissionNames]: boolean;
  };
}

/** The permission flags carried by an organizationPermissions document. */
export type PermissionFlags = {
  [K in (typeof PERMISSION_KEYS)[number]]: boolean;
};

export function buildDefaultPermissions(
  rolePermissions: Partial<PermissionFlags> | null | undefined
): RoleFormData["permissions"] {
  const entries = PERMISSION_KEYS.map(
    (key) => [key, rolePermissions?.[key] ?? false] as const
  );
  return permissionsSchema.parse(Object.fromEntries(entries));
}

export const PermissionsCheckboxes = ({
  control,
  showLabel,
}: {
  control: Control<RoleFormData>;
  showLabel?: boolean;
}) => (
  <fieldset className="space-y-3">
    {showLabel && (
      <legend className="text-foreground mb-3 text-sm font-medium">
        Preset POS Permissions
      </legend>
    )}
    <div className="grid grid-cols-2 gap-4">
      {PERMISSION_KEYS.map((id) => (
        <FormField
          control={control}
          key={id}
          name={`permissions.${id}`}
          render={({ field }) => (
            <FormItem className="flex flex-row items-start gap-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="text-foreground cursor-pointer text-sm leading-tight font-normal">
                {permissionNames[id]}
              </FormLabel>
            </FormItem>
          )}
        />
      ))}
    </div>
  </fieldset>
);

/** Matches the return shape of organizationRoles.getRoleById */
interface RoleWithPermissions {
  _id: Id<"organizationRoles">;
  _creationTime: number;
  name: string;
  organizationId: Id<"organizations">;
  permissionId: Id<"organizationPermissions">;
  type: string;
  permissions: PermissionFlags | null;
}

type RoleFormProps = {
  isInDialog?: boolean;
  onSuccess?: (id: Id<"organizationRoles">) => void;
} & (
  | {
      role: RoleWithPermissions;
      roleName?: undefined;
    }
  | {
      role?: undefined;
      roleName: string;
    }
);

export const RoleForm = ({
  role,
  roleName,
  isInDialog,
  onSuccess,
}: RoleFormProps) => {
  const form = useForm<RoleFormData>({
    values: {
      name: role?.name ?? roleName ?? "New Role",
      permissions: buildDefaultPermissions(role?.permissions),
    },
  });

  const queryClient = useQueryClient();

  const updateRoleMutation = useMutation({
    mutationFn: (params: {
      roleId: Id<"organizationRoles">;
      name: string;
      permissions: { [key in keyof typeof permissionNames]: boolean };
    }) => convexClient.mutation(updateRoleRef, params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["organizationRoles"] });
      void queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });

  const createRoleMutation = useMutation({
    mutationFn: (params: {
      name: string;
      type: "custom";
      permissions: { [key in keyof typeof permissionNames]: boolean };
    }) => convexClient.mutation(createRoleRef, params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["organizationRoles"] });
    },
  });

  const updateRole = async (
    roleId: Id<"organizationRoles">,
    data: RoleFormData
  ) => {
    const result = await updateRoleMutation.mutateAsync({
      roleId,
      name: data.name,
      permissions: data.permissions,
    });
    ProductEvents.roleUpdated(result);
    onSuccess?.(result);
  };

  const createRole = async (data: RoleFormData) => {
    const result = await createRoleMutation.mutateAsync({
      name: data.name,
      type: "custom",
      permissions: data.permissions,
    });
    ProductEvents.roleCreated(result);
    onSuccess?.(result);
  };

  return (
    <Form {...form}>
      <form
        className="space-y-5"
        onSubmit={form.handleSubmit(async (data) => {
          if (updateRoleMutation.isPending || createRoleMutation.isPending) {
            return;
          }
          if (role) {
            await updateRole(role._id, data);
          } else {
            await createRole(data);
          }
        })}
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Role name</FormLabel>
              <FormControl>
                <Input {...field} maxLength={40} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <PermissionsCheckboxes control={form.control} showLabel={true} />
        {isInDialog ? (
          <DialogFooter>
            <DialogClose asChild>
              <Button
                className="w-full"
                size="lg"
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button className="w-full" size="lg" type="submit">
              Create Role
            </Button>
          </DialogFooter>
        ) : (
          <div className="h-10">
            <ShowWithTransition when={form.formState.isDirty}>
              <Button type="submit">Save Changes</Button>
            </ShowWithTransition>
          </div>
        )}
      </form>
    </Form>
  );
};
